import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton';
import WeeklyLineChart from 'components/admin/charts/WeeklyLineChart';
import {
  AI_CITATION_PROVIDER_LABELS,
  AI_CITATION_PROVIDERS,
  AiCitationAnswer,
  useGetAiCitationTrendQuery,
  useGetAiCitationWeekQuery,
  useGetAiCitationWeeksQuery,
  useRunAiCitationsMutation,
} from 'store/services/admin/adminSeoApi';
import { extractErrorMessage } from 'utils/extractErrorMessage';
import './AiSearchTab.css';

interface Props {
  isActive: boolean;
}

const RUNNING_POLL_MS = 20_000;
// After a 200 from the run POST the run row can land a few ms AFTER the
// response (the backend fires the job without awaiting its first write), so the
// invalidation GET can come back without it and nothing would re-poll. Poll
// quickly until the started week appears, then hand off to the running cadence.
const STARTED_POLL_MS = 2_000;
const STARTED_POLL_TIMEOUT_MS = 60_000;

const pct = (share: number | null | undefined): string =>
  share === null || share === undefined ? 'n/a' : `${Math.round(share * 100)}%`;

const delta = (share: number | null, prior: number | null): string => {
  if (share === null || prior === null) return 'n/a';
  const points = Math.round((share - prior) * 100);
  return `${points > 0 ? '+' : ''}${points} pts vs prior week`;
};

/** weekStart is a plain date (Monday in PT); parse it as a local date so it never shifts a day. */
const weekLabel = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

/**
 * AI Search tab of the SEO admin dashboard (Asana 1219288016488651): per
 * provider TradeMagic share with the change vs the prior week, the brand share
 * table, the trend across weeks, the top cited URLs, and every question with
 * its three answers. "Run this week now" starts the current week's run; the
 * weeks list is polled while a run is in progress.
 */
const AiSearchTab: React.FC<Props> = ({ isActive }) => {
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  // The week this tab just started and has not yet seen in the list.
  const [startedWeek, setStartedWeek] = useState<string | null>(null);

  // ONE subscription to the weeks list. It used to be two, the second skipped
  // until a week was running and holding the `refetch` that handleRun called;
  // RTK Query throws on refetch of a skipped query, and that throw landed in
  // handleRun's catch as "Failed to start the run" right after the success
  // toast (prod, 2026-10-27). The polling interval is an option, not a second
  // hook: slow while the latest week is running, fast while a run we started
  // has not shown up yet, otherwise off.
  const [running, setRunning] = useState(false);
  const { data: weeks, isLoading: weeksLoading, error: weeksError } = useGetAiCitationWeeksQuery(undefined, {
    skip: !isActive,
    pollingInterval: running ? RUNNING_POLL_MS : startedWeek ? STARTED_POLL_MS : 0,
  });
  const latest = weeks?.[0];
  useEffect(() => {
    setRunning(latest?.status === 'running');
  }, [latest]);

  // The started week is in the list (running or already complete): stop the fast poll.
  useEffect(() => {
    if (startedWeek && weeks?.some((w) => w.weekStart === startedWeek)) setStartedWeek(null);
  }, [weeks, startedWeek]);
  // Never fast-poll forever if the row never appears.
  useEffect(() => {
    if (!startedWeek) return undefined;
    const t = setTimeout(() => setStartedWeek(null), STARTED_POLL_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [startedWeek]);

  const weekStart = selectedWeek ?? latest?.weekStart ?? null;
  const { data: week, isLoading: weekLoading, error: weekError } = useGetAiCitationWeekQuery(weekStart ?? '', {
    skip: !isActive || !weekStart,
  });
  const { data: trend } = useGetAiCitationTrendQuery(undefined, { skip: !isActive });
  const [runNow, { isLoading: isStarting }] = useRunAiCitationsMutation();

  useEffect(() => {
    if (selectedWeek && weeks && !weeks.some((w) => w.weekStart === selectedWeek)) setSelectedWeek(null);
  }, [weeks, selectedWeek]);

  // Only the POST is inside the try: nothing after a 200 may be reported as a
  // failed start. The mutation's 'AiCitations' invalidation refetches the list;
  // the fast poll covers the case where that refetch beats the run row.
  const handleRun = async () => {
    let res: { weekStart: string; status: 'running' | 'complete' };
    try {
      res = await runNow().unwrap();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Failed to start the run'));
      return;
    }
    if (res.status === 'complete') {
      toast.success('This week is already complete');
      return;
    }
    toast.success(`Run started for the week of ${weekLabel(res.weekStart)}`);
    setStartedWeek(res.weekStart);
  };

  const toggleQuestion = (key: string) =>
    setOpenQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const trendRows = useMemo(() => (trend ?? []).map((p) => ({ ...p })), [trend]);

  if (weeksLoading) return <div className="seo-loading">Loading AI Search data...</div>;
  if (weeksError) {
    return (
      <div className="seo-error-state">
        <h3>Failed to load AI Search weeks</h3>
        <p>{extractErrorMessage(weeksError, 'Please try again later.')}</p>
      </div>
    );
  }

  // A week is complete only when every configured provider answered every
  // question; a week left running (errored or missing answers) is re-run by the
  // same button, which retries the failed rows. So only a complete week, or a
  // POST in flight, disables it.
  const runDisabled = isStarting || latest?.status === 'complete';

  return (
    <div className="ai-search-tab" data-testid="ai-search-tab">
      <div className="ai-search-toolbar">
        <label className="ai-search-week">
          <span>Week</span>
          <select
            className="ai-search-week-select"
            value={weekStart ?? ''}
            onChange={(e) => setSelectedWeek(e.target.value || null)}
            disabled={!weeks || weeks.length === 0}
            aria-label="Week"
          >
            {(weeks ?? []).map((w) => (
              <option key={w.weekStart} value={w.weekStart}>
                {weekLabel(w.weekStart)} · {w.status === 'running' ? 'running' : `${w.answerCount} answers`}
              </option>
            ))}
            {(!weeks || weeks.length === 0) && <option value="">No weeks yet</option>}
          </select>
        </label>
        <div className="ai-search-run">
          {running && (
            <span className="ai-search-muted" data-testid="ai-search-running-note">
              This week is still running; a run retries failed answers.
            </span>
          )}
          <MagicButton
            label={isStarting ? 'Starting…' : 'Run this week now'}
            onPress={handleRun}
            color="purple"
            isLoading={isStarting}
            isDisabled={runDisabled}
          />
        </div>
      </div>

      {!weekStart ? (
        <div className="seo-empty-state">
          <h3>No weekly run recorded yet.</h3>
          <p>Use “Run this week now” to record the first week.</p>
        </div>
      ) : weekLoading ? (
        <div className="seo-loading">Loading week...</div>
      ) : weekError || !week ? (
        <div className="seo-error-state">
          <h3>Failed to load this week</h3>
          <p>{extractErrorMessage(weekError, 'Please try again later.')}</p>
        </div>
      ) : (
        <>
          {/* (a) TradeMagic share per provider */}
          <div className="seo-stats seo-stats-3" data-testid="ai-search-shares">
            {AI_CITATION_PROVIDERS.map((provider) => {
              const row = week.providers.find((p) => p.provider === provider);
              return (
                <div className="seo-stat-card" key={provider} data-testid={`share-${provider}`}>
                  <div className="seo-stat-label">{AI_CITATION_PROVIDER_LABELS[provider]}</div>
                  <div className="seo-stat-value">{pct(row?.tradeMagicShare)}</div>
                  <div className="seo-stat-sub">{row ? delta(row.tradeMagicShare, row.priorWeekShare) : 'n/a'}</div>
                </div>
              );
            })}
          </div>

          {/* (b) Brand share table */}
          <div className="seo-table-card">
            <h3 className="ai-search-section-title">Brand share of answers</h3>
            <table className="seo-table ai-search-brand-table" data-testid="ai-search-brands">
              <thead>
                <tr>
                  <th>Brand</th>
                  {AI_CITATION_PROVIDERS.map((p) => (
                    <th key={p}>{AI_CITATION_PROVIDER_LABELS[p]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {week.brands.map((b) => (
                  <tr key={b.brand} className={b.brand === 'TradeMagic' ? 'ai-search-brand-row--us' : ''}>
                    <td>{b.brand}</td>
                    {AI_CITATION_PROVIDERS.map((p) => (
                      <td key={p}>{pct(b.byProvider[p])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* (c) Trend */}
          <WeeklyLineChart
            title="TradeMagic share by provider"
            subtitle="Share of each provider's answers that mention or cite TradeMagic, per weekly run"
            series={AI_CITATION_PROVIDERS.map((p) => ({ key: p, label: AI_CITATION_PROVIDER_LABELS[p] }))}
            data={trendRows}
            yFormat={(v) => `${Math.round(v * 100)}%`}
            yDomain={[0, 1]}
          />

          {/* (d) Top URLs */}
          <div className="seo-table-card">
            <h3 className="ai-search-section-title">Top cited URLs this week</h3>
            {week.topUrls.length === 0 ? (
              <p className="ai-search-muted">No sources were cited this week.</p>
            ) : (
              <ol className="ai-search-urls" data-testid="ai-search-urls">
                {week.topUrls.slice(0, 20).map((u) => (
                  <li key={u.url}>
                    <a href={u.url} target="_blank" rel="noopener noreferrer" title={u.url}>
                      {u.url}
                    </a>
                    <span className="ai-search-url-count">{u.count}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* (e) Questions and answers */}
          <div className="seo-table-card">
            <h3 className="ai-search-section-title">Questions ({week.questions.length})</h3>
            <div className="ai-search-questions" data-testid="ai-search-questions">
              {week.questions.map((q) => {
                const open = openQuestions.has(q.questionKey);
                return (
                  <div className="ai-search-question" key={q.questionKey}>
                    <button
                      type="button"
                      className="ai-search-question-toggle"
                      aria-expanded={open}
                      aria-controls={`ai-q-${q.questionKey}`}
                      onClick={() => toggleQuestion(q.questionKey)}
                    >
                      <span className="ai-search-question-key">{q.questionKey}</span>
                      <span className="ai-search-question-text">{q.questionText}</span>
                      <span className="ai-search-question-hits">
                        {q.answers.filter((a) => a.brandHits.some((h) => h.brand === 'TradeMagic' && (h.inText || h.inCitations))).length}/
                        {q.answers.length} mention us
                      </span>
                      <span className="ci-chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
                    </button>
                    {open && (
                      <div className="ai-search-answers" id={`ai-q-${q.questionKey}`}>
                        {q.answers.map((a) => (
                          <AnswerCard key={a.provider} answer={a} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const AnswerCard: React.FC<{ answer: AiCitationAnswer }> = ({ answer }) => (
  <article className="ai-search-answer" data-testid={`answer-${answer.provider}`}>
    <header className="ai-search-answer-head">
      <strong>{AI_CITATION_PROVIDER_LABELS[answer.provider] ?? answer.provider}</strong>
      <span className="ai-search-muted">{answer.model}</span>
    </header>
    {answer.error ? (
      <p className="ai-search-answer-error">Error: {answer.error}</p>
    ) : (
      <p className="ai-search-answer-text">{answer.answerText}</p>
    )}
    {answer.brandHits.length > 0 && (
      <div className="ai-search-chips">
        {answer.brandHits.map((h) => (
          <span key={h.brand} className={`ai-search-chip${h.brand === 'TradeMagic' ? ' ai-search-chip--us' : ''}`}>
            {h.brand}
            {h.inCitations ? ' · cited' : ''}
            {h.inText ? ' · in text' : ''}
          </span>
        ))}
      </div>
    )}
    {answer.sources.length > 0 && (
      <ul className="ai-search-sources">
        {answer.sources.map((url, i) => (
          <li key={`${url}-${i}`}>
            <a href={url} target="_blank" rel="noopener noreferrer" title={url}>
              {hostOf(url)}
            </a>
          </li>
        ))}
      </ul>
    )}
  </article>
);

export default AiSearchTab;
